// FrogView — renders a puppet SVG and drives its mouth.
//
// The SVG's #mouth group holds one shape per viseme (id="viseme-*"). We hide
// them all and reveal exactly one at a time. Model viseme ids (0..11, from the
// lip-sync engine) map to SVG ids via VISEME_TO_SVG (same mapping the upstream
// visualizer uses). Defaults to the bundled talking frog, but any SVG with the
// same viseme ids can be passed in (custom puppets).
import frogSvg from './frogSvg';

const VISEME_TO_SVG = {
	0: 'viseme-closed', 1: 'viseme-ah', 2: 'viseme-d', 3: 'viseme-e',
	4: 'viseme-f', 5: 'viseme-l', 6: 'viseme-m', 7: 'viseme-oh',
	8: 'viseme-r', 9: 'viseme-s', 10: 'viseme-uh', 11: 'viseme-oo',
};

export default class FrogView {
	constructor(container, svgMarkup) {
		container.innerHTML = svgMarkup || frogSvg;
		this.svg = container.querySelector('svg');
		if(this.svg) {
			// Let CSS control the size; keep the intrinsic aspect ratio.
			this.svg.removeAttribute('width');
			this.svg.removeAttribute('height');
			// Full width, natural (square) height — the widget's .frog box clips
			// the bottom of the body via overflow:hidden so only the top shows.
			this.svg.style.width = '100%';
			this.svg.style.height = 'auto';
			this.svg.style.display = 'block';
		}

		this.mouthEls = {};
		const mouth = this.svg?.querySelector('#mouth');
		if(mouth) {
			mouth.querySelectorAll('[id^="viseme-"]').forEach(el => {
				el.style.display = 'none';
				this.mouthEls[el.id] = el;
			});
		}
		// Resting mouth: a friendly grin if the artwork has one, else closed.
		this.idleId = this.mouthEls['viseme-grin'] ? 'viseme-grin' : 'viseme-closed';
		this._current = null;
		this.setIdle();

		// The SVG ships a hidden "#blink" layer (closed-eye lids). Flashing it on
		// briefly reads as a blink. Blink at random intervals for a bit of life.
		this.blinkGroup = this.svg?.querySelector('#blink');
		if(this.blinkGroup) this.blinkGroup.style.display = 'none';
		this._blinkTimer = null;
		this._blinkHideTimer = null;
		this.startBlinking();
	}

	_show(svgId) {
		if(this._current === svgId) return;
		if(this._current && this.mouthEls[this._current]) this.mouthEls[this._current].style.display = 'none';
		const el = this.mouthEls[svgId] || this.mouthEls['viseme-closed'];
		if(el) {
			el.style.display = '';
			this._current = svgId;
		} else {
			this._current = null;
		}
	}

	// Show the mouth shape for a model viseme id (0..11).
	setViseme(modelId) { this._show(VISEME_TO_SVG[modelId] ?? 'viseme-closed'); }

	setClosed() { this._show('viseme-closed'); }
	setIdle() { this._show(this.idleId); }

	// ── Blinking ────────────────────────────────────────────────────────────

	// Close the eyes for `closeMs`, then reopen.
	blink(closeMs = 130) {
		if(!this.blinkGroup) return;
		this.blinkGroup.style.display = 'inline';
		clearTimeout(this._blinkHideTimer);
		this._blinkHideTimer = setTimeout(() => { this.blinkGroup.style.display = 'none'; }, closeMs);
	}

	_scheduleBlink() {
		const delay = 2000 + Math.random() * 4000; // next blink in 2–6 s
		this._blinkTimer = setTimeout(() => {
			this.blink();
			if(Math.random() < 0.25) setTimeout(() => this.blink(), 280); // occasional double-blink
			this._scheduleBlink();
		}, delay);
	}

	startBlinking() {
		if(this._blinkTimer || !this.blinkGroup) return;
		this._scheduleBlink();
	}

	stopBlinking() {
		clearTimeout(this._blinkTimer);
		clearTimeout(this._blinkHideTimer);
		this._blinkTimer = null;
		if(this.blinkGroup) this.blinkGroup.style.display = 'none';
	}

	destroy() { this.stopBlinking(); }
}
